// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as apiLogin, fetchRolesPermissions } from '../utils/api'; // Your existing API functions
import { User, AuthContextType } from '../utils/types'; // Your defined types

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [loadingAuth, setLoadingAuth] = useState<boolean>(true);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        const storedUserJson = localStorage.getItem('user');
        if (
            token &&
            storedUserJson &&
            storedUserJson !== "undefined" // <-- add this check
        ) {
            try {
                const storedUser: User = JSON.parse(storedUserJson);
                setUser(storedUser);
                setIsAuthenticated(true);
            } catch (error) {
                console.error("Failed to parse stored user:", error);
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('customer');
                localStorage.removeItem('rolesPermissions');
            }
        }
        setLoadingAuth(false);
    }, []);

    const clearAuthData = useCallback(() => {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('customer');
        localStorage.removeItem('rolesPermissions');
    }, []);

    const handleSuccessfulLogin = (
        appToken: string,
        userData: User,
        rolesPermsData: any, // Adjust type as per your rolesPermissions structure
        customerData?: any   // Adjust type for customer data
    ) => {
        localStorage.setItem('token', appToken);
        localStorage.setItem('user', JSON.stringify(userData));
        if (rolesPermsData) {
            localStorage.setItem('rolesPermissions', JSON.stringify(rolesPermsData));
        }
        if (customerData) {
            localStorage.setItem('customer', JSON.stringify(customerData));
        }

        setUser(userData);
        setIsAuthenticated(true);


        // Centralized role-based navigation
        const userRole = rolesPermsData?.roles?.[0];
        if (userRole && (userRole.toLowerCase() === 'super_admin' || userRole.toLowerCase() === 'staff')) {
            navigate('/customer-management');
        } else {
            navigate('/');
        }

        window.location.reload()

    };

    const logout = useCallback(() => {
        clearAuthData();
        // Consider navigating to login page if not handled by the component calling logout
        navigate('/');
        window.location.reload()
    }, [navigate, clearAuthData]);

    // Internal function to handle Google token sign-in
    const loginWithGoogleToken = async (idToken: string): Promise<User> => {
        try {
            const apiUrl = window.APP_CONFIG?.REACT_APP_API_URL || 'https://t1.u8.my/api';
            const response = await fetch(`${apiUrl}/auth/google/token-signin`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ token: idToken }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Google sign-in failed with status: ${response.status}`);
            }

            // Ensure your Laravel backend returns these fields:
            // access_token: Your application's JWT/Sanctum token
            // user: The user object
            // rolesPermissions: The roles and permissions object for the user
            // customer: The associated customer object (optional)
            const { access_token: appToken, user: userData, rolesPermissions, customer: customerData } = await response.json();

            if (!appToken || !userData) {
                throw new Error("Google login response missing application token or user data.");
            }

            handleSuccessfulLogin(appToken, userData, rolesPermissions, customerData);
            return userData;

        } catch (error) {
            clearAuthData(); // Clean up on error
            console.error("AuthContext Google login failed:", error);
            throw error; // Re-throw for the component to handle
        }
    };


    // New function to complete login after phone OTP verification (called from Login.tsx)
    const completePhoneOtpLogin = useCallback((
        appToken: string,
        userData: User,
        rolesPermsData: any,
        customerData?: any
    ) => {
        // This function reuses the existing logic for setting up the authenticated state
        handleSuccessfulLogin(appToken, userData, rolesPermsData, customerData);
        // No need to return a promise here unless specifically needed by the caller for further chaining
    }, [navigate]); // navigate is a dependency for handleSuccessfulLogin

    // Unified login function
    const login = useCallback(async (identifier: string, password?: string, isGoogleLogin: boolean = false): Promise<User> => {
        if (isGoogleLogin) {
            // Identifier is the Google ID token
            return loginWithGoogleToken(identifier);

        } else {
            // Identifier is email, password is the password
            const email = identifier;

            if (!password) {
                clearAuthData(); // Clean up
                throw new Error("Password is required for email/password login.");
            }

            try {
                const response = await apiLogin(email, password); // Your existing API call

                const loggedInUser: User = response.data.user;
                const customerData = response.data.customer; // Assuming your API returns this
                const appToken = response.data.access_token || loggedInUser.remember_token; // Prefer access_token if available

                if (!loggedInUser || !appToken) {
                    throw new Error("Login response missing user data or application token.");
                }

                // Fetch roles/permissions separately for email/password login
                let rolesPermissionsData = null;

                try {
                    rolesPermissionsData = await fetchRolesPermissions();
                } catch (permError) {
                    console.warn("Failed to fetch roles/permissions after email/password login:", permError);
                    // Decide if login should proceed without roles/permissions or throw error
                }

                handleSuccessfulLogin(appToken, loggedInUser, rolesPermissionsData, customerData);
                return loggedInUser;

            } catch (error) {
                clearAuthData(); // Clean up
                console.error("AuthContext email/password login failed:", error);
                throw error; // Re-throw for the component to handle
            }
        }
    }, [navigate, logout, clearAuthData]); // Added navigate and logout to dependencies

    if (loadingAuth) {
        // You can return a more sophisticated loading spinner/component here
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading authentication...</div>;
    }

    return (
        // <AuthContext.Provider value={{ isAuthenticated, user, login, logout, loadingAuth }}>
        <AuthContext.Provider value={{ isAuthenticated, user, login, logout, loadingAuth, completePhoneOtpLogin }}>

            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    // You might need to update AuthContextType to include completePhoneOtpLogin if it's not already flexible

    return context;
};
